import React from 'react';
import TrendUpIcon from '@/assets/icons/TrendUp.svg';
import MonthPicker from '@/components/MonthPicker';
import { cn } from '@/lib/utils';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { formatNumber } from '@/lib/utils';
import dayjs from 'dayjs';
import DownloadIcon from '@/assets/icons/Download.svg';
import { CreateApiIntroduce } from '../ApiKeys';
import { useRequest } from 'ahooks';
import { getSecretKey } from '@/service/apiKey';
import { getInvoices, getOverviewMonthlyDetail, InvoicesDto, MonthlyDetailDto } from '@/service/overview';
import BellIcon from '@/assets/icons/Bell.svg';
import SpendingProgressBar from './components/SpendingProgressBar';
import { useOverview } from './hooks/useOverview';
import { useApiKeys } from '../ApiKeys/hooks/useApiKey';
import { useNavigate } from 'react-router-dom';
import { CardTitle } from '@/components/ui/card';
import { getDownloadInvoiceLink } from '@/service/billing';

interface OverviewProps {}

const Overview: React.FC<OverviewProps> = () => {
  const { data: apiKeyListData, loading } = useRequest(getSecretKey);
  const { data: overviewData, refresh: refreshOverview } = useRequest(getOverviewMonthlyDetail);
  const { data: invoicesData } = useRequest(getInvoices);
  const { setMonthlyLimitAlertModal } = useOverview();
  const navigate = useNavigate();

  const showCreateApi = !(apiKeyListData?.data?.keys || [])?.length;

  return (
    <div>
      <OverviewHeader />
      <Line />
      <OverviewMatrix
        setMonthlyLimitAlertModal={async () => {
          await setMonthlyLimitAlertModal();
          await refreshOverview();
        }}
        overviewData={overviewData?.data}
      />
      {!loading &&
        (showCreateApi ? (
          <CreateApiIntroduce
            onCreate={async () => {
              navigate('/api-keys');
            }}
          />
        ) : (
          <InvoiceList invoices={invoicesData?.data} />
          // <InvoiceList
          //   invoices={[
          //     {
          //       id: 2,
          //       is_paid: true,
          //       monthlySpend: 0.56,
          //       secondsGenerated: 840,
          //       tracksGenerated: 14,
          //       createdAt: '2025-04-01T00:42:27.300Z',
          //       uniqueUsers: 1,
          //     },
          //   ]}
          // />
        ))}
    </div>
  );
};

const OverviewHeader = () => {
  return (
    <div className="p-6 lg:px-8 lg:py-6 flex justify-between items-center">
      <CardTitle>Overview</CardTitle>
      <MonthPicker />
    </div>
  );
};

const OverviewMatrix = ({
  overviewData,
  setMonthlyLimitAlertModal,
}: {
  overviewData?: MonthlyDetailDto;
  setMonthlyLimitAlertModal: () => Promise<any>;
}) => {
  return (
    <div className="p-4 lg:p-8 lg:pb-5 grid grid-cols-1 lg:grid-cols-3 lg:grid-rows-[177px_102px] gap-5">
      <MatrixBox className="lg:row-span-1 lg:col-span-2 flex flex-col justify-between gap-6">
        <MatrixTitle>Monthly spend</MatrixTitle>
        <div className="flex flex-col justify-between gap-3">
          <div className="text-jen-content-tertiary flex items-center gap-[6px]">
            {/* {overviewData?.monthlySpend ? (
                <>
                  <TrendUpIcon fillColor="#0f9100" />
                  +$2,398 from last month
                </>
              ) : (
                <EmptyNumberSkeleton />
              )} */}
            <EmptyNumberSkeleton />
          </div>
          <div className="text-jen-content-primary text-[54px] lg:text-[64px] leading-none">
            {overviewData?.monthlySpend ? `$${formatNumber(overviewData.monthlySpend)}` : <EmptyNumberSkeleton />}
          </div>
        </div>
      </MatrixBox>
      <MatrixBox className="lg:row-span-2 lg:col-span-1 ext-jen-content-secondary flex flex-col justify-between">
        <div>
          <div className="flex justify-between">
            <div> Monthly Limit Alert</div>
            <div
              onClick={() => {
                setMonthlyLimitAlertModal();
              }}
              className=" underline flex items-center gap-[6px] cursor-pointer"
            >
              <BellIcon />
              Set Limit
            </div>
          </div>
          <div className="text-[36px]">
            {overviewData?.monthlyLimitAlert ? `$${formatNumber(overviewData.monthlyLimitAlert)}` : <EmptyNumberSkeleton />}
          </div>

          <div className="text-jen-content-tertiary h-[18px]">
            {!!overviewData?.daysRemaining && (
              <>
                <span className="text-jen-content-primary">{overviewData?.daysRemaining}</span> Days remaining
              </>
            )}
          </div>
        </div>

        <SpendingProgressBar monthlyLimitAlert={overviewData?.monthlyLimitAlert} monthlySpend={overviewData?.monthlySpend} />
      </MatrixBox>
      <MatrixBox className="lg:row-span-1 lg:col-span-1  flex flex-col justify-between gap-6">
        <MatrixTitle>Seconds Generated</MatrixTitle>
        <h2 className="text-[26px] leading-none">
          {overviewData?.secondsGenerated ? formatNumber(overviewData.secondsGenerated) : <EmptyNumberSkeleton />}
        </h2>
      </MatrixBox>
      <MatrixBox className="lg:row-span-1 lg:col-span-1  flex flex-col justify-between gap-6">
        <MatrixTitle>Tracks Generated</MatrixTitle>
        <h2 className="text-[26px] leading-none">
          {overviewData?.tracksGenerated ? formatNumber(overviewData?.tracksGenerated) : <EmptyNumberSkeleton />}
        </h2>
      </MatrixBox>
    </div>
  );
};

export const InvoiceList = ({ invoices }: { invoices?: InvoicesDto[] }) => {
  return (
    <div className="pt-8">
      <div className="text-jen-content-primary text-[20px] leading-[24px] pl-8 pb-4">Invoices</div>
      <Table>
        <TableHeader>
          <TableRow className="font-pressura-mono text-[#747479] text-[12px] ">
            <TableHead className="pl-8">DATE</TableHead>
            <TableHead className="whitespace-nowrap">NO. TRACKS</TableHead>
            <TableHead className="whitespace-nowrap">NO. SECONDS</TableHead>
            <TableHead className="whitespace-nowrap">TOTAL SPEND</TableHead>
            <TableHead></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {invoices?.map(({ id, monthlySpend, secondsGenerated, tracksGenerated, uniqueUsers, createdAt, is_paid }) => {
            return (
              <TableRow>
                <TableCell className="pl-8 whitespace-nowrap">{dayjs(createdAt).format('MMM DD, YYYY')}</TableCell>
                <TableCell>{formatNumber(tracksGenerated)}</TableCell>
                <TableCell>{formatNumber(secondsGenerated)}</TableCell>
                <TableCell>${formatNumber(monthlySpend)}</TableCell>
                {is_paid && (
                  <TableCell
                    className="flex gap-[6px] items-center text-jen-content-tertiary cursor-pointer"
                    onClick={async () => {
                      const {
                        data: { url },
                      } = await getDownloadInvoiceLink(id);
                      // const url =
                      //   'https://pay.stripe.com/invoice/acct_1POX8CHZ6up2mJYp/test_YWNjdF8xUE9YOENIWjZ1cDJtSllwLF9TREhFc0VTVEZVY3ZtbXRBeWNYT3M5aDlOaHpIZjc2LDEzNjQyMTQzMQ0200AfC832rt/pdf?s=ap';
                      window.open(url);
                    }}
                  >
                    <DownloadIcon fillColor="currentColor" />
                    download
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {!invoices?.length && <div className=" text-jen-content-tertiary text-[16px] w-full p-8 text-center">No Billing History</div>}
    </div>
  );
};

const EmptyNumberSkeleton = () => {
  return <div className="text-jen-disabled">—————</div>;
};

const Line = () => {
  return <div className="border-b border-jen-border-primary w-full"></div>;
};

const MatrixBox: React.FC<{ className?: string; children: React.ReactNode }> = ({ children, className }) => {
  return (
    <div className={cn('h-full  bg-jen-background-secondary rounded-lg p-4 border border-jen-border-primary', className)}>{children}</div>
  );
};

const MatrixTitle: React.FC<{ className?: string; children: React.ReactNode }> = ({ children, className }) => {
  return <div className={cn('text-jen-content-secondary leading-none text-[16px]', className)}>{children}</div>;
};

export default Overview;
